import React, { CSSProperties, ReactNode, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DialogBase from '../../../base/DialogBase/DialogBase';
import {
    useGetProfileStatsQuery,
    useGetMyAdminClientsQuery,
    useGetMyAdminProjectsQuery,
    useGetMyAdminLibrariesQuery,
    UserRoleEnum,
} from '../../../generated/graphql';
import { useCurrentUser } from '../../../App.hooks';
import { useAuth0 } from '@auth0/auth0-react';
import {
    FolderKanban,
    Shield,
    Library,
    Users,
    Box,
    FileText,
    Calendar,
    TrendingUp,
    LogOut,
    ChevronRight,
    Search,
    Loader2,
    ArrowUpDown,
    ChevronDown,
} from 'lucide-react';

type ProfileDialogProps = {
    onClose: () => void;
    visible: boolean;
    /** When provided, show this user's profile instead of the current user */
    userId?: string;
    userName?: string;
    userEmail?: string;
    userRole?: string;
    userCreatedTime?: string;
    style?: CSSProperties | undefined;
    children?: ReactNode | undefined;
};

type StatCardProps = {
    icon: React.ComponentType<any>;
    label: string;
    value: number | string;
    sublabel?: string;
    accentColor?: string;
};

const StatCard = ({ icon: Icon, label, value, sublabel, accentColor }: StatCardProps) => (
    <div
        className="flex flex-col gap-2 rounded-xl p-4 transition-all duration-150"
        style={{
            backgroundColor: 'var(--surface-50)',
            border: '1px solid var(--border-default)',
        }}
    >
        <div className="flex items-center gap-2">
            <div
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{
                    backgroundColor: accentColor ? `${accentColor}18` : 'var(--accent-soft)',
                    color: accentColor || 'var(--accent)',
                }}
            >
                <Icon size={16} strokeWidth={1.5} />
            </div>
            <span className="text-xs font-medium" style={{ color: 'var(--text-tertiary)' }}>
                {label}
            </span>
        </div>
        <div className="flex items-baseline gap-2 pl-1">
            <span className="text-2xl font-bold tracking-tight" style={{ color: 'var(--text-primary)' }}>
                {value}
            </span>
            {sublabel && (
                <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                    {sublabel}
                </span>
            )}
        </div>
    </div>
);

/* ── Reusable empty state for admin tabs ── */
const AdminTabEmpty = ({ icon: Icon, label }: { icon: React.ComponentType<any>; label: string }) => (
    <div className="empty-state" style={{ paddingTop: '3rem', paddingBottom: '3rem' }}>
        <div className="empty-state-icon">
            <Icon size={24} />
        </div>
        <span className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
            You are not an admin of any {label}.
        </span>
    </div>
);

/* ── Reusable loading spinner for admin tabs ── */
const AdminTabLoading = () => (
    <div className="flex items-center justify-center py-12">
        <Loader2 size={24} className="animate-spin" style={{ color: 'var(--text-tertiary)' }} />
    </div>
);

/* ── Reusable search input for admin tabs ── */
const AdminTabSearch = ({ value, onChange }: { value: string; onChange: (v: string) => void }) => (
    <div className="relative mb-3">
        <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2"
            style={{ color: 'var(--text-tertiary)' }}
        />
        <input
            type="text"
            placeholder="Filter…"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full text-sm pl-8 pr-3 py-2 rounded-lg"
            style={{
                border: '1px solid var(--border-default)',
                background: 'var(--bg-input)',
                color: 'var(--text-primary)',
                outline: 'none',
            }}
        />
    </div>
);

/* ── Clickable row for admin entity lists ── */
type AdminListItemProps = {
    icon: React.ComponentType<any>;
    accentColor: string;
    name: string;
    subtitle?: string;
    badge?: React.ReactNode;
    onClick: () => void;
};

const AdminListItem = ({ icon: Icon, accentColor, name, subtitle, badge, onClick }: AdminListItemProps) => (
    <button
        onClick={onClick}
        className="flex items-center gap-3 w-full text-left px-3 py-2.5 rounded-lg transition-colors duration-100"
        style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'inherit',
        }}
        onMouseOver={(e) => { e.currentTarget.style.backgroundColor = 'var(--bg-hover)'; }}
        onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
    >
        <div
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${accentColor}18`, color: accentColor }}
        >
            <Icon size={16} strokeWidth={1.5} />
        </div>
        <div className="flex flex-col min-w-0 flex-1">
            <span className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                {name}
            </span>
            {subtitle && (
                <span className="text-xs truncate" style={{ color: 'var(--text-tertiary)' }}>
                    {subtitle}
                </span>
            )}
        </div>
        {badge}
        <ChevronRight size={14} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
    </button>
);

/* ══════════════════════════════════════════════
   Tab: My Clients
   ══════════════════════════════════════════════ */
const MyClientsTab = ({ userId, onNavigate }: { userId?: string; onNavigate: (path: string) => void }) => {
    const { data, loading } = useGetMyAdminClientsQuery({ variables: { userId }, skip: !userId });
    const [search, setSearch] = useState('');

    const clients = useMemo(() => {
        const all = data?.ClientList?.nodes?.filter(Boolean) ?? [];
        if (!search) return all;
        const q = search.toLowerCase();
        return all.filter(c => c?.name?.toLowerCase().includes(q) || c?.longName?.toLowerCase().includes(q));
    }, [data, search]);

    if (loading) return <AdminTabLoading />;
    if (!clients.length && !search) return <AdminTabEmpty icon={Users} label="clients" />;

    return (
        <div>
            <AdminTabSearch value={search} onChange={setSearch} />
            <div className="flex flex-col gap-0.5">
                {clients.map(client => client && (
                    <AdminListItem
                        key={client.id!}
                        icon={Users}
                        accentColor="#f59e0b"
                        name={client.name || ''}
                        subtitle={client.longName || client.description || undefined}
                        onClick={() => onNavigate(`/clients/${client.id}`)}
                    />
                ))}
                {clients.length === 0 && search && (
                    <span className="text-sm py-4 text-center" style={{ color: 'var(--text-tertiary)' }}>
                        No clients match "{search}"
                    </span>
                )}
            </div>
        </div>
    );
};

/* ── Project health helpers ── */
type HealthData = { Name: string; Group: string; Limit: number; Value: number; AdditionalData: string };

const classifyHealthItem = (value: number, limit: number) => {
    if (limit <= 0) return 'na';
    const pct = value / limit;
    if (pct <= 1.0) return 'passing';
    if (pct < 1.3) return 'warning';
    return 'failing';
};

const calcProjectHealth = (models: Array<{ healthResults?: string | null } | null> | null | undefined): number | null => {
    if (!models?.length) return null;
    let totalPassing = 0, totalChecks = 0;
    for (const m of models) {
        if (!m?.healthResults) continue;
        try {
            const results: HealthData[] = JSON.parse(m.healthResults);
            for (const r of results) {
                const s = classifyHealthItem(r.Value, r.Limit);
                if (s !== 'na') {
                    totalChecks++;
                    if (s === 'passing') totalPassing++;
                }
            }
        } catch { /* skip */ }
    }
    if (totalChecks === 0) return null;
    return Math.round((totalPassing / totalChecks) * 100);
};

const ProjectHealthChip = ({ score, loading }: { score: number | null; loading?: boolean }) => {
    if (loading) {
        return (
            <span
                className="animate-pulse inline-block rounded-full"
                style={{ width: 48, height: 18, backgroundColor: 'var(--border-default)', flexShrink: 0 }}
            />
        );
    }
    if (score === null) return null;
    if (score >= 80) {
        return (
            <span
                className="inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-xs font-semibold flex-shrink-0"
                style={{ backgroundColor: '#dcfce7', color: '#15803d' }}
                title={`Health: ${score}%`}
            >
                <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', backgroundColor: '#16a34a' }} />
                {score}%
            </span>
        );
    }
    if (score >= 50) {
        return (
            <span
                className="inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-xs font-semibold flex-shrink-0"
                style={{ backgroundColor: '#fef9c3', color: '#a16207' }}
                title={`Health: ${score}%`}
            >
                <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', backgroundColor: '#ca8a04' }} />
                {score}%
            </span>
        );
    }
    return (
        <span
            className="inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-xs font-semibold flex-shrink-0"
            style={{ backgroundColor: '#fee2e2', color: '#b91c1c' }}
            title={`Health: ${score}%`}
        >
            <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', backgroundColor: '#dc2626' }} />
            {score}%
        </span>
    );
};

/* ── Sort options for projects ── */
type ProjectSortOption = 'name' | 'newest' | 'updated';
const projectSortLabels: Record<ProjectSortOption, string> = {
    name: 'Name (A–Z)',
    newest: 'Newest Created',
    updated: 'Recently Updated',
};

/* ══════════════════════════════════════════════
   Tab: My Projects
   ══════════════════════════════════════════════ */
const MyProjectsTab = ({ userId, onNavigate }: { userId?: string; onNavigate: (path: string) => void }) => {
    const { data, loading } = useGetMyAdminProjectsQuery({ variables: { userId }, skip: !userId });
    const [search, setSearch] = useState('');
    const [sortBy, setSortBy] = useState<ProjectSortOption>('newest');
    const [sortDropdownOpen, setSortDropdownOpen] = useState(false);

    const projects = useMemo(() => {
        let all = data?.ProjectList?.nodes?.filter(Boolean) ?? [];
        if (search) {
            const q = search.toLowerCase();
            all = all.filter(p =>
                p?.name?.toLowerCase().includes(q) ||
                p?.client?.name?.toLowerCase().includes(q) ||
                p?.library?.name?.toLowerCase().includes(q)
            );
        }
        const sorted = [...all];
        switch (sortBy) {
            case 'name':
                sorted.sort((a, b) => (a?.name ?? '').localeCompare(b?.name ?? ''));
                break;
            case 'newest':
                sorted.sort((a, b) => Number(b?.createdTime ?? 0) - Number(a?.createdTime ?? 0));
                break;
            case 'updated':
                sorted.sort((a, b) => Number(b?.updatedTime ?? 0) - Number(a?.updatedTime ?? 0));
                break;
        }
        return sorted;
    }, [data, search, sortBy]);

    // Show full spinner only on the very first load (no cached data yet)
    if (loading && !data) return <AdminTabLoading />;
    if (!projects.length && !search && !loading) return <AdminTabEmpty icon={FolderKanban} label="projects" />;

    return (
        <div>
            <div className="flex items-center gap-2 mb-3">
                <div className="flex-1">
                    <AdminTabSearch value={search} onChange={setSearch} />
                </div>
                <div className="relative" style={{ marginBottom: '0.75rem' }}>
                    <button
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap"
                        style={{ backgroundColor: 'var(--surface-100)', color: 'var(--text-secondary)', border: 'none', cursor: 'pointer', outline: 'none' }}
                        onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
                    >
                        <ArrowUpDown size={14} /> {projectSortLabels[sortBy]} <ChevronDown size={12} />
                    </button>
                    {sortDropdownOpen && (
                        <div className="absolute top-full right-0 mt-1 card p-1.5 z-50 min-w-40" style={{ boxShadow: 'var(--shadow-lg)' }}>
                            {(Object.entries(projectSortLabels) as [ProjectSortOption, string][]).map(([key, label]) => (
                                <button
                                    key={key}
                                    className="w-full text-left px-3 py-2 rounded-md text-sm transition-colors"
                                    style={{
                                        backgroundColor: sortBy === key ? 'var(--accent-soft)' : 'transparent',
                                        color: sortBy === key ? 'var(--accent)' : 'var(--text-primary)',
                                        border: 'none',
                                        cursor: 'pointer',
                                    }}
                                    onMouseEnter={e => { if (sortBy !== key) e.currentTarget.style.backgroundColor = 'var(--bg-hover)'; }}
                                    onMouseLeave={e => { if (sortBy !== key) e.currentTarget.style.backgroundColor = 'transparent'; }}
                                    onClick={() => { setSortBy(key); setSortDropdownOpen(false); }}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            <div className="flex flex-col gap-0.5">
                {projects.map(project => project && (
                    <AdminListItem
                        key={project.id!}
                        icon={FolderKanban}
                        accentColor="#8b5cf6"
                        name={project.name || ''}
                        subtitle={[project.client?.name, project.library?.name].filter(Boolean).join(' · ') || undefined}
                        badge={<ProjectHealthChip score={calcProjectHealth(project.models)} loading={loading} />}
                        onClick={() => onNavigate(`/projects/${project.id}`)}
                    />
                ))}
                {projects.length === 0 && search && (
                    <span className="text-sm py-4 text-center" style={{ color: 'var(--text-tertiary)' }}>
                        No projects match "{search}"
                    </span>
                )}
            </div>
        </div>
    );
};

/* ══════════════════════════════════════════════
   Tab: My Libraries
   ══════════════════════════════════════════════ */
const MyLibrariesTab = ({ userId, onNavigate }: { userId?: string; onNavigate: (path: string) => void }) => {
    const { data, loading } = useGetMyAdminLibrariesQuery({ variables: { userId }, skip: !userId });
    const [search, setSearch] = useState('');

    const libraries = useMemo(() => {
        const all = data?.LibraryList?.nodes?.filter(Boolean) ?? [];
        if (!search) return all;
        const q = search.toLowerCase();
        return all.filter(l => l?.name?.toLowerCase().includes(q) || l?.description?.toLowerCase().includes(q));
    }, [data, search]);

    if (loading) return <AdminTabLoading />;
    if (!libraries.length && !search) return <AdminTabEmpty icon={Library} label="libraries" />;

    return (
        <div>
            <AdminTabSearch value={search} onChange={setSearch} />
            <div className="flex flex-col gap-0.5">
                {libraries.map(lib => lib && (
                    <AdminListItem
                        key={lib.id!}
                        icon={Library}
                        accentColor="#ec4899"
                        name={lib.name || ''}
                        subtitle={lib.description || undefined}
                        onClick={() => onNavigate(`/clients/${lib.clientId}/library/${lib.id}`)}
                    />
                ))}
                {libraries.length === 0 && search && (
                    <span className="text-sm py-4 text-center" style={{ color: 'var(--text-tertiary)' }}>
                        No libraries match "{search}"
                    </span>
                )}
            </div>
        </div>
    );
};

/* ══════════════════════════════════════════════
   Main Dialog
   ══════════════════════════════════════════════ */
const ProfileDialog = (props: ProfileDialogProps) => {
    const { visible, onClose, userId: propsUserId, userName: propsUserName, userEmail: propsUserEmail, userRole: propsUserRole, userCreatedTime: propsUserCreatedTime } = props;
    const currentUser = useCurrentUser();
    const { logout } = useAuth0();
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState(0);

    const handleLogout = useCallback(() => {
        // Clear cached tokens/state before redirecting to Auth0 logout
        localStorage.clear();
        sessionStorage.clear();
        logout({ logoutParams: { returnTo: window.location.origin } });
    }, [logout]);

    // Use provided props, otherwise fall back to the current user
    const isExternalUser = !!propsUserId;
    const targetUserId = propsUserId ?? currentUser?.id;
    const targetUserName = propsUserName ?? currentUser?.name;
    const targetUserEmail = propsUserEmail ?? currentUser?.email;
    const targetUserRole = propsUserRole ?? currentUser?.role;
    const targetUserCreatedTime = propsUserCreatedTime ?? currentUser?.createdTime;

    const now = new Date();

    // Build month keys for past 3 months (oldest first)
    const monthKeys = useMemo(() => {
        const months: { year: number; month: number; label: string }[] = [];
        for (let i = 2; i >= 0; i--) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
            months.push({
                year: d.getFullYear(),
                month: d.getMonth(),
                label: d.toLocaleString('default', { month: 'short', year: 'numeric' }),
            });
        }
        return months;
    }, [visible]);

    const { data, loading } = useGetProfileStatsQuery({
        variables: {
            userId: targetUserId,
        },
        skip: !visible || !targetUserId,
    });

    // Helper: convert epoch-seconds string to { year, month }
    const ticksToYearMonth = (ticks: string | null | undefined): { year: number; month: number } | null => {
        if (!ticks) return null;
        const t = new Date(1970, 0, 1);
        t.setUTCSeconds(Number.parseInt(ticks) - t.getTimezoneOffset() * 60);
        return { year: t.getFullYear(), month: t.getMonth() };
    };

    const stats = useMemo(() => {
        if (!data) return null;
        return {
            memberProjectCount: data.MemberProjects?.nodes?.length ?? 0,
            adminProjectCount: data.AdminProjects?.nodes?.length ?? 0,
            adminLibraryCount: data.AdminLibraries?.nodes?.length ?? 0,
            adminClientCount: data.AdminClients?.nodes?.length ?? 0,
            modelsTotal: data.AllMyModels?.nodes?.length ?? 0,
            contentsTotal: data.AllMyContents?.nodes?.length ?? 0,
        };
    }, [data]);

    const chartData = useMemo(() => {
        if (!data) return { categories: [] as string[], models: [] as number[], contents: [] as number[], maxValue: 0 };

        const countByMonth = (nodes: Array<{ createdTime?: string | null } | null | undefined> | null | undefined) => {
            return monthKeys.map(mk => {
                return (nodes ?? []).filter(n => {
                    if (!n?.createdTime) return false;
                    const ym = ticksToYearMonth(n.createdTime);
                    return ym !== null && ym.year === mk.year && ym.month === mk.month;
                }).length;
            });
        };

        const models = countByMonth(data.AllMyModels?.nodes);
        const contents = countByMonth(data.AllMyContents?.nodes);
        const maxValue = Math.max(...models, ...contents, 1);

        return {
            categories: monthKeys.map(m => m.label),
            models,
            contents,
            maxValue,
        };
    }, [data, monthKeys]);

    const initials = targetUserName
        ?.split(' ')
        .map((n: string) => n[0])
        .join('')
        .substring(0, 2) ?? '';

    const roleName = targetUserRole && targetUserRole.toLowerCase() !== 'none'
        ? targetUserRole.charAt(0).toUpperCase() + targetUserRole.slice(1).toLowerCase()
        : 'User';

    const memberSince = useMemo(() => {
        if (!targetUserCreatedTime) return '';
        const d = new Date(1970, 0, 1);
        d.setUTCSeconds(Number.parseInt(targetUserCreatedTime) - d.getTimezoneOffset() * 60);
        return d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    }, [targetUserCreatedTime]);

    /* Navigate to an entity and close the dialog */
    const handleNavigate = useCallback((path: string) => {
        onClose();
        navigate(path);
    }, [onClose, navigate]);

    /* Tab definitions — show admin tabs for own profile or when viewing as system admin */
    const isCurrentUserAdmin = currentUser?.role === UserRoleEnum.Admin;
    const showAdminTabs = !isExternalUser || isCurrentUserAdmin;

    const tabs = useMemo(() => {
        const base = [
            { label: 'Overview', icon: TrendingUp },
        ];
        if (showAdminTabs) {
            base.push(
                { label: 'Clients', icon: Users },
                { label: 'Projects', icon: FolderKanban },
                { label: 'Libraries', icon: Library },
            );
        }
        return base;
    }, [showAdminTabs]);

    return (
        <DialogBase
            title={isExternalUser ? 'User Profile' : 'My Profile'}
            type="Custom"
            isLoading={loading && activeTab === 0}
            visible={visible}
            onClose={onClose}
            width="620px"
        >
            {/* Profile Header */}
            <div className="flex items-center gap-4 mb-5">
                <div className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--surface-200)' }}>
                    <span className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{initials}</span>
                </div>
                <div className="flex flex-col min-w-0 flex-1">
                    <span className="text-lg font-semibold tracking-tight" style={{ color: 'var(--text-primary)' }}>
                        {targetUserName}
                    </span>
                    <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                        {targetUserEmail}
                    </span>
                    <div className="flex items-center gap-3 mt-1">
                        <span
                            className="text-xs font-medium px-2 py-0.5 rounded-full"
                            style={{
                                backgroundColor: 'var(--accent-soft)',
                                color: 'var(--accent)',
                            }}
                        >
                            {roleName}
                        </span>
                        {memberSince && (
                            <span className="text-xs flex items-center gap-1" style={{ color: 'var(--text-tertiary)' }}>
                                <Calendar size={12} />
                                Member since {memberSince}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Tab bar */}
            <div className="tab-bar mb-4" style={{ alignSelf: 'flex-start', display: 'inline-flex' }}>
                {tabs.map((tab, i) => {
                    const Icon = tab.icon;
                    return (
                        <button
                            key={tab.label}
                            onClick={() => setActiveTab(i)}
                            className={activeTab === i ? 'tab-item-active' : 'tab-item'}
                        >
                            <Icon size={14} strokeWidth={1.5} />
                            {tab.label}
                        </button>
                    );
                })}
            </div>

            {/* ─── Tab: Overview ─── */}
            {activeTab === 0 && (
                <>
                    {/* Section: Projects */}
                    <div className="mb-4">
                        <div className="flex items-center gap-2 mb-3">
                            <TrendingUp size={14} style={{ color: 'var(--text-tertiary)' }} />
                            <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--text-tertiary)' }}>
                                Project Involvement
                            </span>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <StatCard
                                icon={FolderKanban}
                                label="Member Of"
                                value={stats?.memberProjectCount ?? '–'}
                                sublabel="projects"
                                accentColor="#3b82f6"
                            />
                            <StatCard
                                icon={Shield}
                                label="Admin Of"
                                value={stats?.adminProjectCount ?? '–'}
                                sublabel="projects"
                                accentColor="#8b5cf6"
                            />
                            <StatCard
                                icon={Users}
                                label="Client Admin"
                                value={stats?.adminClientCount ?? '–'}
                                sublabel="clients"
                                accentColor="#f59e0b"
                            />
                        </div>
                    </div>

                    {/* Section: Activity Chart */}
                    <div className="mb-4">
                        <div className="flex items-center gap-2 mb-3">
                            <TrendingUp size={14} style={{ color: 'var(--text-tertiary)' }} />
                            <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--text-tertiary)' }}>
                                Activity – Last 3 Months
                            </span>
                        </div>
                        <div
                            className="rounded-xl p-4"
                            style={{
                                backgroundColor: 'var(--surface-50)',
                                border: '1px solid var(--border-default)',
                            }}
                        >
                            {/* Legend */}
                            <div className="flex items-center justify-center gap-5 mb-4">
                                <div className="flex items-center gap-1.5">
                                    <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#10b981' }} />
                                    <span className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Models</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#06b6d4' }} />
                                    <span className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Content</span>
                                </div>
                            </div>

                            {/* Chart */}
                            {(() => {
                                const BAR_ZONE = 90; // px — fixed height for the bar area only
                                const maxVal = chartData.maxValue;
                                const barH = (val: number) =>
                                    maxVal > 0 ? Math.max(Math.round((val / maxVal) * BAR_ZONE), val > 0 ? 8 : 2) : 2;
                                return (
                                    <div className="flex items-end gap-6 justify-around">
                                        {chartData.categories.map((label, i) => {
                                            const modelVal = chartData.models[i];
                                            const contentVal = chartData.contents[i];
                                            return (
                                                <div key={label} className="flex flex-col items-center gap-2 flex-1">
                                                    {/* Fixed-height bar zone — bars grow from the bottom */}
                                                    <div className="flex items-end gap-1.5 w-full justify-center" style={{ height: BAR_ZONE }}>
                                                        <div className="flex flex-col items-end gap-1" style={{ width: 32 }}>
                                                            <span className="text-xs font-semibold" style={{ color: '#10b981' }}>
                                                                {modelVal}
                                                            </span>
                                                            <div
                                                                className="w-full rounded-t-md transition-all duration-500"
                                                                style={{
                                                                    height: barH(modelVal),
                                                                    backgroundColor: '#10b981',
                                                                }}
                                                            />
                                                        </div>
                                                        <div className="flex flex-col items-end gap-1" style={{ width: 32 }}>
                                                            <span className="text-xs font-semibold" style={{ color: '#06b6d4' }}>
                                                                {contentVal}
                                                            </span>
                                                            <div
                                                                className="w-full rounded-t-md transition-all duration-500"
                                                                style={{
                                                                    height: barH(contentVal),
                                                                    backgroundColor: '#06b6d4',
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                    <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>{label}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })()}

                            {/* Totals */}
                            <div className="flex items-center justify-center gap-6 mt-3 pt-3" style={{ borderTop: '1px solid var(--border-default)' }}>
                                <div className="flex items-center gap-2">
                                    <Box size={14} color="#10b981" />
                                    <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                                        <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{stats?.modelsTotal ?? '–'}</span> models total
                                    </span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <FileText size={14} color="#06b6d4" />
                                    <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                                        <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{stats?.contentsTotal ?? '–'}</span> content total
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Library Admin */}
                    <div className="mb-2">
                        <div className="grid grid-cols-1 gap-3">
                            <StatCard
                                icon={Library}
                                label="Library Admin"
                                value={stats?.adminLibraryCount ?? '–'}
                                sublabel="libraries"
                                accentColor="#ec4899"
                            />
                        </div>
                    </div>
                </>
            )}

            {/* ─── Tab: My Clients ─── */}
            {activeTab === 1 && <MyClientsTab userId={targetUserId ?? undefined} onNavigate={handleNavigate} />}

            {/* ─── Tab: My Projects ─── */}
            {activeTab === 2 && <MyProjectsTab userId={targetUserId ?? undefined} onNavigate={handleNavigate} />}

            {/* ─── Tab: My Libraries ─── */}
            {activeTab === 3 && <MyLibrariesTab userId={targetUserId ?? undefined} onNavigate={handleNavigate} />}
        </DialogBase>
    );
};

export default ProfileDialog;
